/**
 * React Query hooks for API calls
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cartApi, checkoutApi, ordersApi, customersApi, sessionsApi } from "./client";
import type { CreateOrderRequest, UpdateCartRequest, RemoveFromCartRequest } from "@/types";

/**
 * Query keys
 */
export const queryKeys = {
  cart: (sessionId: string) => ["cart", sessionId] as const,
  shippingOptions: () => ["shipping-options"] as const,
  order: (orderId: string) => ["order", orderId] as const,
  session: (sessionId: string) => ["session", sessionId] as const,
};

/**
 * Hook to fetch cart
 */
export function useCart(sessionId: string | null) {
  return useQuery({
    queryKey: sessionId ? queryKeys.cart(sessionId) : (["cart"] as const),
    queryFn: () => cartApi.getCart(sessionId!),
    enabled: !!sessionId,
    staleTime: 0, // Always fetch fresh data
    retry: 2,
  });
}

/**
 * Hook to update cart item
 */
export function useUpdateCartItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateCartRequest) => cartApi.updateItem(request),
    onSuccess: (data) => {
      // Update cart in cache
      queryClient.setQueryData(queryKeys.cart(data.session_id), data);
    },
  });
}

/**
 * Hook to remove cart item
 */
export function useRemoveCartItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: RemoveFromCartRequest) => cartApi.removeItem(request),
    onSuccess: (data) => {
      // Update cart in cache
      queryClient.setQueryData(queryKeys.cart(data.session_id), data);
    },
  });
}

/**
 * Hook to fetch shipping options
 */
export function useShippingOptions() {
  return useQuery({
    queryKey: queryKeys.shippingOptions(),
    queryFn: () => checkoutApi.getShippingOptions(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create order
 */
export function useCreateOrder() {
  const queryClient = useQueryClient();

  console.log("1");

  return useMutation({
    mutationFn: (request: CreateOrderRequest) => checkoutApi.createOrder(request),
    onMutate: (request) => {
      console.log("onMutate", request);
    },
    onSuccess: () => {
      // Invalidate cart cache after order creation
      queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
    onError: (error) => {
      console.log("onError", error);
    },
  });
}

/**
 * Hook to fetch order details
 */
export function useOrder(orderId: string | null) {
  return useQuery({
    queryKey: orderId ? queryKeys.order(orderId) : (["order"] as const),
    queryFn: () => ordersApi.getOrder(orderId!),
    enabled: !!orderId,
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useCustomerLookup() {
  return useMutation({
    mutationFn: ({ phoneNumber, retailerId }: { phoneNumber: string; retailerId: string }) =>
      customersApi.lookupByPhone(phoneNumber, retailerId),
  });
}

/**
 * Hook to fetch session details with attribution context
 */
export function useSession(sessionId: string | null) {
  return useQuery({
    queryKey: sessionId ? queryKeys.session(sessionId) : (["session"] as const),
    queryFn: () => sessionsApi.getSession(sessionId!),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
